import type { ConceptId, SourceReference } from "./types";

export type { ConceptId } from "./types";

export interface ConceptDefinition {
  id: ConceptId;
  title: string;
  plain: string;
  meaningful: string;
  check: string[];
  caution?: string;
  sources: SourceReference[];
}

const REVIEWED = "2026-07-28";
const official = (
  label: string,
  authority: string,
  url: string,
  jurisdiction: SourceReference["jurisdiction"] = "federal",
  verify = false,
): SourceReference => ({
  label,
  authority,
  url,
  jurisdiction,
  reviewedAt: REVIEWED,
  verify,
});

export const concepts: Record<ConceptId, ConceptDefinition> = {
  "ahv-gap": {
    id: "ahv-gap",
    title: "What is an AHV contribution gap?",
    plain: "Your AHV individual account records contribution years and insured income. A missing year can reduce the first-pillar pension paid throughout retirement.",
    meaningful: "A statement check is useful after moving countries, studying, taking a career break, changing employers or becoming self-employed. Some recent gaps may still be correctable; older gaps often cannot simply be repaid.",
    check: [
      "Order the free individual-account statement.",
      "Compare each year with employment, residence and contribution evidence.",
      "Ask the compensation office to investigate errors or a recent gap.",
    ],
    caution: "Do not enter your AHV number in CH/OS. Use it only in the official request.",
    sources: [
      official("Request an individual-account statement", "OASI/DI Information Centre", "https://www.ahv-iv.ch/en/Forms/Statement-of-the-individual-account"),
      official("What to do about contribution gaps", "OASI/DI Information Centre", "https://www.ahv-iv.ch/de/News-Infos/post/was-tun-bei-beitragsluecken", "federal", true),
    ],
  },
  "pillar2-buyin": {
    id: "pillar2-buyin",
    title: "What is a Pillar 2 buy-in?",
    plain: "A voluntary buy-in pays money into an occupational pension fund when its certificate shows unused purchase capacity.",
    meaningful: "A buy-in may improve retirement benefits and can often be tax-deductible, but liquidity, pension-fund quality, withdrawal plans and personal tax circumstances matter. Capacity is permission to assess—not an automatic recommendation.",
    check: [
      "Use the current fund certificate and ask the fund to confirm capacity in writing.",
      "Compare retirement, disability and survivor effects—not only the tax deduction.",
      "Check planned home, emigration or retirement capital withdrawals before paying.",
    ],
    caution: "A capital withdrawal within three years after a buy-in can create tax consequences. Confirm the individual case with the pension fund and tax authority.",
    sources: [
      official("Occupational pension overview", "Federal Social Insurance Office", "https://www.bsv.admin.ch/en/old-age-provision-occupational-benefits-plan", "federal", true),
      official("Second pillar overview", "Swiss Confederation · ch.ch", "https://www.ch.ch/en/retirement/old-age-pension/the-2nd-pillar/", "federal", true),
    ],
  },
  "pillar3a": {
    id: "pillar3a",
    title: "What is Pillar 3a?",
    plain: "Pillar 3a is restricted private retirement saving for people with eligible earned income. Contributions can reduce taxable income within the annual limit.",
    meaningful: "The useful decision is not only whether to contribute, but how much liquidity you can lock away, whether cash or invested 3a fits the time horizon, and how fees and risk compare.",
    check: [
      "Confirm eligibility and the current annual maximum.",
      "Keep near-term money outside 3a.",
      "Compare fees, investment allocation, withdrawal rules and provider safeguards.",
    ],
    caution: "Provider examples in CH/OS are commercial examples, never an endorsement or universal ranking.",
    sources: [
      official("Third-pillar overview", "Swiss Confederation · ch.ch", "https://www.ch.ch/en/taxes-and-finances/old-age-pension/3rd-pillar"),
      official("Current Pillar 3a amounts", "Federal Social Insurance Office", "https://www.bsv.admin.ch/de/beitrag-3-sauele"),
    ],
  },
  "tax-at-source": {
    id: "tax-at-source",
    title: "What does taxed at source mean?",
    plain: "Tax is withheld from salary by the employer. That does not always settle every deduction, income or wealth reporting obligation.",
    meaningful: "Income level, other income, wealth and requested deductions can trigger or justify a subsequent ordinary assessment. The request rules and deadlines matter.",
    check: [
      "Confirm whether ordinary assessment is mandatory for your circumstances.",
      "Keep 3a, professional-expense and other deduction evidence.",
      "Use the cantonal tax process for the actual filing or request.",
    ],
    caution: "A voluntary request can lead to a full ordinary assessment. Verify the consequence before submitting.",
    sources: [
      official("Subsequent ordinary assessment", "Federal Tax Administration", "https://www.estv.admin.ch/dam/estv/de/dokumente/dbst/kreisschreiben/dbst-ks-2019-1-045-d-de.pdf.download.pdf/dbst-ks-2019-1-045-d-de.pdf", "federal", true),
    ],
  },
  "cash-buffer": {
    id: "cash-buffer",
    title: "What counts as a cash buffer?",
    plain: "Accessible money reserved for shocks—not investments, a tax reserve or money already assigned to a known bill.",
    meaningful: "Its job is to prevent an insurance deductible, job interruption or urgent repair from forcing debt or an investment sale at the wrong time.",
    check: [
      "Estimate essential monthly spending.",
      "Subtract tax and planned-spending reserves from accessible cash.",
      "Choose a personal runway based on income stability and household risk.",
    ],
    caution: "Three months is a planning signal used by CH/OS, not a universal target.",
    sources: [],
  },
  "savings-rate": {
    id: "savings-rate",
    title: "What is your savings rate?",
    plain: "CH/OS estimates it as monthly net income minus total monthly spending, divided by monthly net income.",
    meaningful: "It shows how much flexibility exists for resilience, pensions and investing. It is descriptive, not a score: childcare, study, a move or a planned career break can make a temporarily low rate entirely rational.",
    check: [
      "Use an average month rather than the best month.",
      "Include annual costs by dividing them across twelve months.",
      "Review after a salary, housing or household change.",
    ],
    sources: [],
  },
  "investment-horizon": {
    id: "investment-horizon",
    title: "What is an investment horizon?",
    plain: "The earliest time you may need the money for its intended purpose—not your age or how long you hope to keep a broker account.",
    meaningful: "Equities can fall sharply and recover slowly. Money needed for tax, a home deposit or another near-term goal should not depend on a timely market recovery.",
    check: [
      "Separate each goal by date.",
      "Keep emergency and near-term spending outside long-term risk assets.",
      "Use the shortest relevant horizon when one pot has several jobs.",
    ],
    sources: [
      official("Collective investment risk information", "Swiss Financial Market Supervisory Authority", "https://www.finma.ch/en/documentation/dossier/dossier-anlegerschutz/"),
    ],
  },
  "risk-capacity": {
    id: "risk-capacity",
    title: "Risk capacity is not the same as risk appetite",
    plain: "Risk appetite is how comfortable a loss feels. Risk capacity is whether your finances and timeline can absorb it without changing the plan.",
    meaningful: "A high tolerance cannot make near-term money safe in equities; a long horizon alone cannot compensate for unstable cash flow or leverage.",
    check: [
      "Stress-test a large temporary portfolio fall.",
      "Check income stability, debt and near-term goals.",
      "Choose a plan you could follow during bad markets.",
    ],
    sources: [],
  },
  rebalancing: {
    id: "rebalancing",
    title: "What is rebalancing?",
    plain: "Returning a portfolio toward its intended mix after market movements change the weights.",
    meaningful: "It makes risk control systematic instead of reacting to headlines. New contributions can often rebalance with less selling.",
    check: [
      "Write down target ranges, not only exact percentages.",
      "Review at a fixed interval or when a range is breached.",
      "Consider taxes, transaction costs and product fees before trading.",
    ],
    sources: [],
  },
  "broker-custody": {
    id: "broker-custody",
    title: "How should you compare a broker?",
    plain: "Price matters, but so do regulation, custody, currency conversion, tax documents, product access, support and operational complexity.",
    meaningful: "The cheapest-looking trade can be a poor fit if the account encourages leverage, creates difficult records or lacks the service you need.",
    check: [
      "Verify the legal entity and regulator serving Swiss residents.",
      "Compare total costs: custody, trades, foreign exchange and product fees.",
      "Confirm statements support your Swiss tax return.",
    ],
    caution: "IBKR is included as one widely used commercial example, not as a universal recommendation.",
    sources: [
      official("Authorised institutions and products", "Swiss Financial Market Supervisory Authority", "https://www.finma.ch/en/finma-public/authorised-institutions-individuals-and-products/"),
    ],
  },
  w8ben: {
    id: "w8ben",
    title: "What does Form W-8BEN do?",
    plain: "A non-US individual generally gives Form W-8BEN to the broker or payer to certify foreign status and claim treaty treatment where available.",
    meaningful: "For US dividends, a valid form can let the payer apply the appropriate treaty withholding rate. It is not the Swiss reclaim itself and it is not normally sent with the Swiss tax return.",
    check: [
      "Confirm the broker has a valid form for the correct account holder.",
      "Review after a change in tax residence or other circumstances.",
      "Do not use W-8BEN if you are a US person; a different form can apply.",
    ],
    caution: "A W-8BEN is generally valid through 31 December of the third calendar year after signing unless circumstances change.",
    sources: [
      official("Switzerland–USA tax forms", "Federal Tax Administration", "https://www.estv.admin.ch/de/usa"),
      official("Instructions for Form W-8BEN", "US Internal Revenue Service", "https://www.irs.gov/instructions/iw8", "federal", true),
    ],
  },
  da1: {
    id: "da1",
    title: "What is DA-1?",
    plain: "DA-1 is the Swiss request to credit eligible non-refundable foreign withholding tax on declared foreign dividends or interest.",
    meaningful: "It can prevent part of foreign withholding from becoming a permanent cost, but eligibility, minimums, documentation and the cantonal workflow matter.",
    check: [
      "Keep annual broker tax statements and gross income/withholding details.",
      "Declare the underlying foreign income and assets correctly.",
      "File DA-1 through the canton’s current tax-return process.",
    ],
    caution: "There is no single CH/OS deadline for every canton. Verify the filing route and deadline with the cantonal authority. Additional US tax retained through a Swiss qualified intermediary may involve R-US 164.",
    sources: [
      official("Direct federal tax forms: DA-1 and R-US 164", "Federal Tax Administration", "https://www.estv.admin.ch/de/formulare-wegleitungen-direkte-bundessteuer", "federal", true),
      official("Switzerland–USA tax forms", "Federal Tax Administration", "https://www.estv.admin.ch/de/usa"),
    ],
  },
  "professional-securities-trading": {
    id: "professional-securities-trading",
    title: "When can investing be treated as professional trading?",
    plain: "Private capital gains are generally tax-free, but facts such as frequent trading, short holding periods, leverage, derivatives and reliance on trading income can change the analysis.",
    meaningful: "The Federal Tax Administration’s safe-harbour criteria are cumulative. Missing one does not automatically mean professional status, but it makes an individual assessment possible.",
    check: [
      "Review turnover, holding periods, leverage and derivatives use.",
      "Keep complete trade and financing records.",
      "Get qualified advice before a high-frequency or leveraged strategy.",
    ],
    caution: "CH/OS cannot determine professional-trader status.",
    sources: [
      official("Circular 36: professional securities trading", "Federal Tax Administration", "https://www.estv.admin.ch/dam/de/sd-web/oOz28af293pZ/dbst-ks-2012-1-036-d-de.pdf", "federal", true),
    ],
  },
  mfk: {
    id: "mfk",
    title: "What is the MFK vehicle inspection?",
    plain: "The periodic cantonal roadworthiness inspection is commonly called MFK in German-speaking Switzerland.",
    meaningful: "The authority—not the app—issues the binding invitation and date. Registration age and inspection history help CH/OS estimate when to watch for it.",
    check: [
      "Record first registration and the latest official inspection.",
      "Keep the cantonal invitation as the binding date.",
      "Prepare tyres, lights, brakes, documents and any required maintenance.",
    ],
    sources: [
      official("Cantonal road traffic offices", "Association of Cantonal Road Traffic Offices", "https://asa.ch/strassenverkehrsaemter/adressen/", "cantonal", true),
    ],
  },
};
